import { defineStore } from "pinia";
import { Lesson } from "@/models/Lesson.model";
import { orgApi } from "@/utils/Api.util";
import { useClassroomsStore } from "@/stores/Classrooms.store";
import { ApiErrorMessageBag } from "@/stores/errors/ApiErrorMessageBag.ts";
import { DateTime, Interval } from "luxon";
import SmaSyLocalStorage from "@/utils/SmaSyLocalStorage.util";
import { useUsersStore } from "@/stores/Users.store";
import { AgendaStore, AgendaStoreState } from "@/stores/AbstractAgenda.store";
import { useThemesStore } from "@/stores/Themes.store";
import { LessonItem } from "@/models/LessonItem.model";
import { Pupil } from "@/models/Pupil.model";

interface LessonsStoreState extends AgendaStoreState<Lesson> {

}

const BaseStore = AgendaStore<Lesson, LessonsStoreState>();

export const useLessonsStore = defineStore('lessons', {

	state: (): LessonsStoreState => ({

		errors: null,
		events: [],
		loadingRanges: 0,
		loadedRanges: [],
		lastFetchedRange: null

	}),

	getters: {

		...BaseStore.getters,

		apiPath(state: LessonsStoreState) {
			return 'lessons';
		},

		defaultMask(state: LessonsStoreState) {
			return [
				'id',
				'type',
				'title',
				'description',
				'start',
				'end',
				'color',
				'classroom',
				'seats',
				'lessonItems.*',
				'lessonItems.activity.*',
				'lessonItems.icon.*',
				'lessonItems.icon.library.id',
				'lessonItems.attachments.*',

				// Lesson plans
				'lessonItems.lessonPlan.*',
				'lessonItems.lessonPlan.tags.*',
				'lessonItems.lessonPlan.supplies.*',
				'lessonItems.lessonPlan.attachments.*',
				'lessonItems.lessonPlan.owner.id',
				'lessonItems.lessonPlan.owner.name',
				'lessonItems.lessonPlan.owner.email',
				'lessonItems.lessonPlan.goals.*',
				'lessonItems.lessonPlan.collections.id',
				'lessonItems.lessonPlan.originalCreator.id',
				'lessonItems.lessonPlan.originalCreator.name',
				'lessonItems.lessonPlan.originalCreator.authorType',
				'lessonItems.lessonPlan.originalCreator.publicName',
				'lessonItems.lessonPlan.creator.id',
				'lessonItems.lessonPlan.creator.name',
				'lessonItems.lessonPlan.creator.authorType',
				'lessonItems.lessonPlan.creator.publicName',
			];
		},

		getBetweenForPupil(state: LessonsStoreState) {
			return (start: Date, end: Date, pupil: Pupil) => {
				return state.events.filter(event => {
					if (! (
						event.end.getTime() > start.getTime() && 
						event.start.getTime() <= end.getTime()
					)) {
						return false;
					}

					return event.isForPupil(pupil);
				});
			};
		},

	},

	actions: {

		...BaseStore.actions,

		mapEventFromServer(data: any): Lesson {
			return Lesson.mapFromServer(data);
		},

		new(properties: any = null) {

			const lesson = new Lesson();
			lesson.boardType = '';
			lesson.classroomId = useClassroomsStore().getCurrentClassroom().id.toString();

			if (properties !== null) {
				this._setLessonProperties(lesson, properties);
			}
			return lesson;
		},

		clone(lessonId: string) {

			const lesson = this.getFromId(lessonId);

			let clone = lesson.clone();
			clone.classroomId = useClassroomsStore().getCurrentClassroom().id.toString();
			clone.originalId = lesson.id;

			// Unset the pupil selection for all lesson items, this way the server will automatically assign all available pupils
			// (or not, but the logic is on the server)
			clone.lessonItems.forEach((item: LessonItem) => {
				item.allowedPupilIds = null;
			});

			clone.id = undefined;

			return clone;
		},

		async store(lesson: Lesson) {

			this.clearErrors();

			try {
				const serverData: any = lesson.asApiObject();
				serverData.classroom = {id: lesson.classroomId};

				const response = await orgApi.post(this.apiPath, serverData, {
					params: {
						mask: this.defaultMask.join(',')
					}
				});
				lesson.setFromServerData(response.data.data);
				this.events.push(lesson);

				this.rememberLastLessonInfo(lesson);

			} catch (e: any) {
				this.errors = ApiErrorMessageBag.fromAxiosException(e);
				throw this.errors;
			}

		},

		async update(lesson: Lesson, force = false) {

			this.clearErrors();
			try {

				const body: any = lesson.asApiObject();
				if (force) {
					body.force = force;
				}

				const response = await orgApi.put(this.apiPath + "/" + lesson.id, body, {
					params: {
						mask: this.defaultMask.join(',')
					}
				});
				lesson.setFromServerData(response.data.data);

				this.rememberLastLessonInfo(lesson);

			} catch (e: any) {
				this.errors = ApiErrorMessageBag.fromAxiosException(e);
				throw this.errors;
			}

		},

		async delete(lesson: Lesson) {

			this.clearErrors();

			try {
				// Remove from the API
				await orgApi.delete("lessons/" + lesson.id);
				this.removeFromId(lesson.id);
			} catch (e: any) {
				this.errors = ApiErrorMessageBag.fromAxiosException(e);
				throw this.errors;
			}

		},

		async updateLesson(lessonId, properties, force = false) {
			let lesson = this.getFromId(lessonId);
			this._setLessonProperties(lesson, properties);

			await this.update(lesson, force);
		},

		async clearDay(isoDateString: string) {

			const start = new Date(isoDateString);
			start.setHours(0, 0, 0, 0);

			const end = new Date(start)
			end.setDate(end.getDate() + 1);

			await this.clearDateRange(start, end);
		},

		/**
		 * @param sourceClassroomId
		 * @param source
		 * @param destinations
		 * @param confirmOverride
		 * @param confirmUpdateLessonPlans
		 */
		async copyTo(
			sourceClassroomId: string,
			source: Interval,
			destinations: Interval[],
			confirmOverride: boolean = false,
			confirmUpdateLessonPlans: boolean = false
		) {
			const destinationClassroomId = useClassroomsStore().getCurrentClassroom().id;

			const targets = await Promise.all(
				destinations.map(
					async (destination: Interval): Promise<any> => {

						return {
							start: destination.start.toJSDate().toISOString(),
							end: destination.end.toJSDate().toISOString(),
							classroom: {
								id: destinationClassroomId
							}
						};
					}
				)
			);

			try {
				const post: any = {
					classroomIds: sourceClassroomId,
					start: source.start.toJSDate().toISOString(),
					end: source.end.toJSDate().toISOString(),
					destinations: targets
				}

				if (confirmOverride) {
					post.force = true;
				}

				if(typeof confirmUpdateLessonPlans !== undefined){
					post.updateOutdatedLessonPlans = confirmUpdateLessonPlans;
				}

				// Remove from the API
				const response = await orgApi.post(
				'lessons/duplicate',
					post,
					{
						params: {
							mask: this.defaultMask.join(',')
						}
					}
				);

				this.mergeEvents(response.data);

				let minStart = DateTime.max(...destinations.map(d => d.start));
				let maxEnd = DateTime.min(...destinations.map(d => d.end));

				// Reload the themes as these might have changed
				useThemesStore().loadBetween(minStart.toJSDate(), maxEnd.toJSDate(), null, true);

			} catch (e: any) {
				this.errors = ApiErrorMessageBag.fromAxiosException(e);
				throw this.errors;
			}
		},

		/** Private actions **/

		/**
		 * @param lesson
		 * @param properties
		 */
		_setLessonProperties(lesson: Lesson, properties: any) {

			if(properties.title) {
				lesson.title = properties.title;
			}
			if(properties.color) {
				lesson.color = properties.color;
			}
			if(properties.start) {
				lesson.start = properties.start;
			}
			if(properties.end) {
				lesson.end = properties.end;
			}

		},

		async rememberLastLessonInfo(lesson: Lesson) {

			const user = useUsersStore();
			const me = await user.loadMe();

			if(lesson.boardType === Lesson.BOARD_TYPE_ACTIVITIES || lesson.boardType === Lesson.BOARD_TYPE_REFLECTION) {
				let lastLessonInfo = SmaSyLocalStorage.getUserItem(me.id, 'last_lesson_info');
				if(!lastLessonInfo) {
					lastLessonInfo = {};
				}
				lastLessonInfo[lesson.boardType] = {
					title: lesson.title,
					color: lesson.color,
				}
				SmaSyLocalStorage.setUserItem(me.id, 'last_lesson_info', lastLessonInfo);
			}

		},

		async tryRetrieveLastLessonInfoFor(boardType: string) {

			if(!boardType) {
				return null;
			}

			const user = useUsersStore();
			const me = await user.loadMe();

			let lastLessonInfo = SmaSyLocalStorage.getUserItem(me.id, 'last_lesson_info');
			if(lastLessonInfo && lastLessonInfo[boardType]) {
				return lastLessonInfo[boardType];
			} else {
				return null;
			}
		},

		async loadComplete(lesson: Lesson) {
			this.clearErrors();

			try {
				const response = await orgApi.get(this.apiPath + "/" + lesson.id, {
					params: {
						mask: this.defaultMask.join(',')
					}
				});
				lesson.setFromServerData(response.data.data);
				return lesson;
			} catch (e: any) {
				this.errors = ApiErrorMessageBag.fromAxiosException(e);
				throw this.errors;
			}
		}


	}

});
